---
title: "Turning a prospect into a candidate"
description: "How to import a sourced prospect into your hiring pipeline in Chosen — what data carries over, and the difference between the sourcing pool and the pipeline."
category: "sourcing"
keywords:
  - "import prospect"
  - "prospect to candidate"
  - "sourcing pool"
  - "add prospect to pipeline"
  - "convert prospect"
schema: "HowTo"
nav:
  section: "guides"
  order: 60
  parent: "/guides/sourcing"
---

A prospect lives in the sourcing pool. A candidate lives in your hiring pipeline. Importing is the one-click move from the first to the second — usually after a prospect replies and wants to talk. Their profile data, links, and work history carry over, and a new candidate record appears in your pipeline.

## Sourcing pool versus hiring pipeline

These are two separate places, on purpose:

- **The sourcing pool** is everyone you've ever sourced — the **Prospects** tab. People in it may not know you exist yet. It's a research surface: search it, enrich it, run [outreach](/guides/sourcing/outreach-drafts) from it.
- **The hiring pipeline** is your real candidates for real jobs — the [candidates table and board](/guides/candidates/table-and-boards), with [statuses](/guides/candidates/statuses-and-pipeline), match ratings, and scheduling.

Keeping them apart means your pipeline metrics aren't diluted by hundreds of cold prospects you only emailed once. A prospect becomes a candidate when there's an actual hiring conversation to track — not before.

## Import a prospect

<Steps>
  <li>In the **Search** results or the **Prospects** table, select the prospects you want — tick one, several, or use **Select all**.</li>
  <li>A bar appears at the bottom of the screen. With a role selected in the left sidebar, it reads **Add to Role**; without one, **Add to Candidates**.</li>
  <li>Click it. Each selected prospect becomes a candidate in your pipeline.</li>
  <li>If you imported into a role, the new candidates land at the **lead** stage of that job, ready to move through your pipeline.</li>
</Steps>

You can import one prospect or a batch in the same action. A prospect you've already imported is skipped automatically — re-importing won't create a duplicate.

## What carries over

The new candidate record is built from everything Chosen knows about the prospect:

- **Name, headline, current company, and location.**
- **Profile links** — LinkedIn, GitHub, personal site, and other source URLs.
- **Years of experience** and the assembled work history.
- **Source**, set to "Sourcing", so you can always tell a candidate came from a Mission rather than an application.

After import, Chosen parses the prospect's profile into a structured candidate in the background and computes a [match rating](/guides/candidates/match-rating) against the job — the same scoring an applicant gets. The rating may take a moment to appear while that runs.

<Callout type="info" title="Importing copies the prospect — it doesn't move it">
The original prospect stays in your sourcing pool, now linked to the new candidate. Outreach history and enrichment results stay with the prospect record; the candidate record is for tracking the hire. You're not losing anything by importing — you're adding a pipeline view of someone you already had.
</Callout>

## When it gets it wrong

Because the candidate is built from web-sourced data, the same caveats as the [prospect record](/guides/sourcing/find-prospects) apply — a parsed profile is a strong draft, not a verified resume. Once the candidate is in your pipeline, treat their details as you would any sourced profile: worth a read before you lean on them.

A match rating on an imported candidate is only as sharp as the job description it scored against and the profile Chosen could assemble — see [match rating](/guides/candidates/match-rating) for how that number is built and what it can't see. From here, the candidate behaves like any other: move them through [statuses](/guides/candidates/statuses-and-pipeline) and into [scheduling](/guides/scheduling).
